import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Key, Plus, Trash2, Copy, Eye, EyeOff, Clock, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApiKeysTab = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/api-keys`, { withCredentials: true });
      setKeys(res.data.keys || []);
    } catch {
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Enter a name for the API key");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/api-keys`,
        { name: newKeyName.trim() },
        { withCredentials: true }
      );
      setNewlyCreatedKey(res.data);
      setNewKeyName("");
      setShowKey(true);
      fetchKeys();
      toast.success("API key created");
    } catch {
      toast.error("Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const deleteKey = async (keyId, keyName) => {
    if (!window.confirm(`Delete API key "${keyName}"? Any integration using this key will stop working.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/api-keys/${keyId}`, { withCredentials: true });
      fetchKeys();
      toast.success("API key deleted");
    } catch {
      toast.error("Failed to delete API key");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — please select and copy manually");
    }
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="api-keys-tab">
      <div>
        <h2 className="text-lg font-semibold">API Keys</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Create API keys for external integrations (e.g. Exact Online sync).
          Keys authenticate requests to the sync API.
        </p>
      </div>

      {/* Create new key */}
      <div className="bg-muted/30 border rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-medium">Create new API key</h3>
        <div className="flex gap-2">
          <Input
            placeholder="Key name (e.g. Exact Online Sync)"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createKey()}
            className="max-w-sm"
            data-testid="api-key-name-input"
          />
          <Button onClick={createKey} disabled={creating} data-testid="create-api-key-btn">
            <Plus className="w-4 h-4 mr-1" />
            Create
          </Button>
        </div>
      </div>

      {/* Newly created key - show only once */}
      {newlyCreatedKey && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2" data-testid="new-key-display">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">Store this key now — it won't be shown again</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border rounded px-3 py-2 text-sm font-mono break-all" data-testid="new-key-value">
              {showKey ? newlyCreatedKey.key : "•".repeat(40)}
            </code>
            <Button variant="ghost" size="sm" onClick={() => setShowKey(!showKey)}>
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newlyCreatedKey.key)} data-testid="copy-key-btn">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => setNewlyCreatedKey(null)} className="mt-2">
            Done
          </Button>
        </div>
      )}

      {/* API documentation */}
      <div className="bg-slate-50 border rounded-lg p-4 space-y-3">
        <h3 className="text-sm font-medium">Sync endpoint</h3>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p><code className="bg-white border rounded px-2 py-0.5 text-xs font-mono">POST /api/invoices/purchase/sync</code></p>
          <p>Header: <code className="bg-white border rounded px-2 py-0.5 text-xs font-mono">X-API-Key: your_key_here</code></p>
          <p>Body (JSON):</p>
          <pre className="bg-white border rounded p-3 text-xs font-mono overflow-x-auto">{`{
  "invoices": [
    {
      "invoice_number": "25600493",
      "supplier": "MassInfra B.V.",
      "invoice_date": "2025-09-01",
      "due_date": "2025-10-01",
      "outstanding_amount": 284.35
    }
  ]
}`}</pre>
          <p className="text-xs">Invoices in the request → set to <strong>Niet betaald</strong>. Previously imported invoices not in the request → automatically set to <strong>Betaald</strong>.</p>
        </div>
      </div>

      {/* Existing keys */}
      <div>
        <h3 className="text-sm font-medium mb-3">Active keys ({keys.length})</h3>
        {keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">No API keys yet.</p>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <div key={k.id} className="flex items-center justify-between bg-white border rounded-lg px-4 py-3" data-testid={`api-key-${k.id}`}>
                <div className="flex items-center gap-3">
                  <Key className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{k.name}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="font-mono">{k.key_prefix}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Created {new Date(k.created_at).toLocaleDateString("en-GB")}
                      </span>
                      {k.last_used_at && (
                        <span>Last used {new Date(k.last_used_at).toLocaleDateString("en-GB")}</span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteKey(k.id, k.name)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-key-${k.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeysTab;
